import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Divider,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import DeviationService from './services/deviationService.ts';
import Loader from '../../common/components/Loader.tsx';
import Visibility from '../../common/components/Visibility.tsx';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MedicationIcon from '@mui/icons-material/Medication';
import WarningIcon from '@mui/icons-material/Warning';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InventoryIcon from '@mui/icons-material/Inventory';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { parseTransactionTypeToString } from '../../common/utils/parseTransactionTypeToString';
import { DateTime } from 'luxon';

const DeviationView = () => {
    const { id } = useParams();
    const [deviation, setDeviation] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isChecked, setIsChecked] = useState(false);
    const navigate = useNavigate();
    const deviationService = new DeviationService();
    const [isSaving, setIsSaving] = useState(false);
    const [comment, setComment] = useState('');

    useEffect(() => {
        const fetchDeviation = async () => {
            setIsLoading(true);
            try {
                const response = await deviationService.getDeviation(id);
                setDeviation(response);
            } catch (error) {
                setError('Kunde inte hämta avvikelsen. Försök igen.');
                console.error('Error fetching deviation:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDeviation();
    }, [id]);

    const handleCloseDeviation = async () => {
        const confirmClose = window.confirm('Vill du klarmarkera avvikelsen?');
        if (confirmClose) {
            try {
                setIsSaving(true);
                await deviationService.setStatusDone(id, comment);
                setDeviation({ ...deviation, actionStatus: 2 });
                navigate('..');
            } catch (error) {
                console.error('Error closing deviation:', error);
                setError('Ett fel inträffade var god försök igen.');
            } finally {
                setIsSaving(false);
            }
        } else {
            setIsChecked(false);
        }
    };

    const handleCheckboxChange = () => {
        setIsChecked(!isChecked);
        if (!isChecked) {
            handleCloseDeviation();
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    if (!deviation) {
        return <Typography color="error">Avvikelsen hittades inte.</Typography>;
    }

    const formattedTransactionDate = DateTime.fromISO(
        deviation.transactionRecord.createdOn
    )
        .setLocale('sv-SE')
        .toFormat('yyyy-MM-dd HH:mm');

    const transactionTypeText = parseTransactionTypeToString(
        deviation.transactionRecord.transactionType
    );

    const getStatusText = (status: number) => {
        switch (status) {
            case 0:
                return { text: 'Öppen', color: 'error' };
            case 1:
                return { text: 'Under utredning', color: 'warning' };
            case 2:
                return { text: 'Åtgärdad', color: 'success' };
            default:
                return { text: 'Okänd status', color: 'default' };
        }
    };

    const deviationStatus = getStatusText(deviation.actionStatus);

    const mismatchInfo = {
        reported: deviation.transactionRecord.remainingQuantityAccordingToUser,
        expected:
            deviation.transactionRecord.quantityMismatch > 0
                ? deviation.transactionRecord.remainingQuantityAccordingToUser -
                  deviation.transactionRecord.quantityMismatch
                : deviation.transactionRecord.remainingQuantityAccordingToUser +
                  Math.abs(deviation.transactionRecord.quantityMismatch),
        difference: deviation.transactionRecord.quantityMismatch,
        text:
            deviation.transactionRecord.quantityMismatch > 0
                ? `${deviation.transactionRecord.quantityMismatch} st för mycket`
                : `${Math.abs(deviation.transactionRecord.quantityMismatch)} st för lite`,
    };

    return (
        <div className="p-4 sm:p-6">
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('..')}
                color="primary"
                className="mb-4"
            >
                Tillbaka
            </Button>
            <Paper
                elevation={3}
                sx={{
                    p: { xs: 2, sm: 4, md: 6 },
                    maxWidth: '900px',
                    mx: 'auto',
                    borderRadius: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        flexWrap: 'wrap',
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
                    >
                        Avvikelse
                    </Typography>
                    <Chip
                        icon={
                            deviation.actionStatus === 2 ? (
                                <CheckCircleIcon />
                            ) : (
                                <ErrorIcon />
                            )
                        }
                        label={deviationStatus.text}
                        color={
                            deviationStatus.color as
                                | 'error'
                                | 'warning'
                                | 'success'
                        }
                        sx={{ fontWeight: 'bold' }}
                    />
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Alert
                    severity={mismatchInfo.difference > 0 ? 'warning' : 'error'}
                    icon={<WarningIcon />}
                    sx={{ mb: 3, '& .MuiAlert-message': { width: '100%' } }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{ fontWeight: 'bold' }}
                        >
                            {mismatchInfo.text}
                        </Typography>
                        <Typography variant="body2">
                            ID: {deviation.id}
                        </Typography>
                    </Box>
                </Alert>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography
                                    variant="h6"
                                    component="h2"
                                    gutterBottom
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <MedicationIcon sx={{ mr: 1 }} />{' '}
                                    Läkemedelsinformation
                                </Typography>

                                <Stack spacing={2}>
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                        >
                                            Läkemedel
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 'medium' }}
                                        >
                                            {
                                                deviation.transactionRecord.drug
                                                    .drugName
                                            }{' '}
                                            {
                                                deviation.transactionRecord.drug
                                                    .concentration
                                            }{' '}
                                            {
                                                deviation.transactionRecord.drug
                                                    .concentrationUnit
                                            }
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {
                                                deviation.transactionRecord.drug
                                                    .quantity
                                            }{' '}
                                            {
                                                deviation.transactionRecord.drug
                                                    .quantityUnit
                                            }
                                            ,{' '}
                                            {
                                                deviation.transactionRecord.drug
                                                    .dosageForm
                                            }
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                        >
                                            Lagerställe
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 'medium',
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <InventoryIcon
                                                fontSize="small"
                                                sx={{ mr: 0.5 }}
                                            />
                                            {
                                                deviation.transactionRecord
                                                    .storageLocation.name
                                            }
                                        </Typography>
                                        {deviation.transactionRecord
                                            .storageLocation.location && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Plats:{' '}
                                                {
                                                    deviation.transactionRecord
                                                        .storageLocation
                                                        .location
                                                }
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                        >
                                            Transaktionstyp
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 'medium' }}
                                        >
                                            {transactionTypeText}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography
                                    variant="h6"
                                    component="h2"
                                    gutterBottom
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <ErrorIcon sx={{ mr: 1 }} />{' '}
                                    Avvikelsedetaljer
                                </Typography>

                                <Stack spacing={2}>
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                        >
                                            Rapporterad behållning
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 'medium' }}
                                        >
                                            {mismatchInfo.reported} st
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                        >
                                            Förväntad behållning
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 'medium' }}
                                        >
                                            {mismatchInfo.expected} st
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                        >
                                            Avvikelse
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 'bold',
                                                color:
                                                    mismatchInfo.difference > 0
                                                        ? 'warning.main'
                                                        : 'error.main',
                                            }}
                                        >
                                            {mismatchInfo.text}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                        >
                                            Saldo före
                                        </Typography>
                                        <Typography variant="body1">
                                            {
                                                deviation.transactionRecord
                                                    .saldoBeforeTransaction
                                            }{' '}
                                            st
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                        >
                                            Saldo efter
                                        </Typography>
                                        <Typography variant="body1">
                                            {
                                                deviation.transactionRecord
                                                    .saldoAfterTransaction
                                            }{' '}
                                            st
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography
                                    variant="h6"
                                    component="h2"
                                    gutterBottom
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <PersonIcon sx={{ mr: 1 }} /> Rapportör &
                                    tidsinformation
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography
                                                    variant="subtitle2"
                                                    color="text.secondary"
                                                >
                                                    Rapporterad av
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 'medium',
                                                    }}
                                                >
                                                    {deviation.firstName}{' '}
                                                    {deviation.lastName}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Användarnamn:{' '}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 'medium',
                                                    }}
                                                >
                                                    {deviation.userName}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography
                                                    variant="subtitle2"
                                                    color="text.secondary"
                                                >
                                                    Transaktion skapad
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <CalendarTodayIcon
                                                        fontSize="small"
                                                        sx={{ mr: 0.5 }}
                                                    />
                                                    {formattedTransactionDate}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Grid>
                                    {deviation.actionStatus != 0 && (
                                        <Grid item xs={12} sm={6}>
                                            <Stack spacing={2}>
                                                <Box>
                                                    <Typography
                                                        variant="subtitle2"
                                                        color="text.secondary"
                                                    >
                                                        Kommentar
                                                    </Typography>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                        }}
                                                    >
                                                        {deviation.comment}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {deviation.actionStatus === 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            mt: 3,
                            p: 2,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            gap: 2,
                        }}
                    >
                        <TextField
                            id="comment"
                            label="Kommentar"
                            variant="outlined"
                            multiline
                            minRows={2}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            sx={{
                                flexGrow: 1,
                                maxWidth: '100%',
                                alignSelf: 'stretch',
                            }}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <Visibility visible={!isSaving}>
                                <Checkbox
                                    id="closeDeviation"
                                    checked={isChecked}
                                    onChange={handleCheckboxChange}
                                    color="success"
                                />
                            </Visibility>
                            <Visibility visible={isSaving}>
                                <CircularProgress size={24} />
                            </Visibility>
                            <Typography
                                component="label"
                                htmlFor="closeDeviation"
                                sx={{
                                    fontStyle: 'italic',
                                    ml: 1,
                                    cursor: 'pointer',
                                }}
                            >
                                Klarmarkera avvikelsen
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Paper>
        </div>
    );
};

export default DeviationView;
